"use client";

import { ChatMessageDto, ChatThreadDto } from "@/types/chat";
import { getApiToken, clearApiToken } from "@/lib/token";
import { signOut } from "next-auth/react";
import { getBrowserBase, join } from "@/lib/apiBase";

// Build same-origin URLs; when proxy is enabled, route everything via /api/proxy
// Fallback to proxy when no explicit public API base is provided
// Proxy is opt-in only; default to direct same-origin '/api/*' routes
const useProxy: boolean = (() => {
  return (process.env.NEXT_PUBLIC_USE_PROXY || '').toLowerCase() === 'true';
})();
const U = (p: string) => {
  const path = p.startsWith('/') ? p : `/${p}`;
  if (useProxy) {
    // Always prefix with /api/proxy to avoid cross-origin calls from the browser
    return `/api/proxy${path}`;
  }
  // Fallback: direct to backend base for non /api paths
  return path.startsWith('/api/') ? path : join(getBrowserBase(), path);
};

// Redirect an unauthenticated chat call to the login page — but NEVER when
// already on an auth route, and never carrying the full window.location.href
// as returnTo. Both were latent loop hazards: href wraps any existing
// returnTo and re-encodes it, and without the auth-route guard a chat call
// that fired on /login would re-wrap into an unbounded /login?returnTo loop
// (cf. the 2026-07-29 redirect-loop incident). Mirrors apiFetch/adminFetch.
async function redirectToLoginOnAuthFailure(): Promise<void> {
  if (typeof window === "undefined") return;
  const cur = window.location.pathname;
  if (/^\/(login|register|verify|onboarding)/i.test(cur)) return;
  const rt = encodeURIComponent(`${cur}${window.location.search}`);
  const dest = `/login?returnTo=${rt}`;
  await signOut({ callbackUrl: dest, redirect: true }).catch(() => {
    window.location.href = dest;
  });
}

function withAuth(headers: HeadersInit = {}): Promise<HeadersInit> {
  return getApiToken().then((t) => (t ? { ...headers, Authorization: `Bearer ${t}` } : headers));
}

// Only a 401 means the sign-in is gone. A 403 is the api refusing this one action (the recipient's
// friends-only or post-only setting, a block); signing the member out for it lost the message and the
// session together (2026-10-27). Errors read "<path> <status> <body>" so callers can name the refusal.
async function throwIfNotOk(res: Response, path: string): Promise<void> {
  if (res.status === 401) {
    try {
      clearApiToken();
    } catch {}
    await redirectToLoginOnAuthFailure();
    throw new Error(`${path} ${res.status}`);
  }
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`${path} ${res.status} ${txt}`);
  }
}

async function get<T>(path: string): Promise<T> {
  const headers = await withAuth();
  // Do NOT send cross-origin cookies; bearer is primary
  const res = await fetch(U(path), { headers });
  await throwIfNotOk(res, path);
  return res.json() as Promise<T>;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const headers = await withAuth({ "Content-Type": "application/json" });
  const res = await fetch(U(path), {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  await throwIfNotOk(res, path);
  return res.json() as Promise<T>;
}

// ---------- Public API used by the chat UI ----------

export async function getMyUserId(): Promise<string> {
  const headers = await withAuth();
  // Use same-origin path (proxy when enabled)
  const res = await fetch(U('/api/chat/me'), { headers });
  if (!res.ok) throw new Error(`/api/chat/me ${res.status}`);
  return res.text();
}

export type ThreadBucket = "inbox" | "requests";
export async function fetchThreads(skip = 0, take = 30, bucket: ThreadBucket = "inbox"): Promise<ChatThreadDto[]> {
  const qs = new URLSearchParams({ skip: String(skip), take: String(take) });
  if (bucket) qs.set("bucket", bucket);
  return get<ChatThreadDto[]>(`/api/chat/threads?${qs.toString()}`);
}

export async function fetchMessages(
  otherUserId: string,
  skip = 0,
  take = 50
): Promise<ChatMessageDto[]> {
  return get<ChatMessageDto[]>(
    `/api/chat/messages/${encodeURIComponent(otherUserId)}?skip=${encodeURIComponent(skip)}&take=${encodeURIComponent(take)}`
  );
}

export async function sendMessage(req: {
  toId: string;
  message: string;
  messageType?: "text" | "image";
  clientMessageId?: number;
  // A real Posts.PostId, the post this message applies to. Persisted as
  // UserComLog.PostId and read as the match signal, so never send a library id here.
  postId?: number | null;
  // A chat-library image id, image sends only. Authorizes the image server-side.
  libraryImageId?: number | null;
}): Promise<ChatMessageDto> {
  // Server expects {toId, message, messageType, clientMessageId?, postId?, libraryImageId?}
  return postJson<ChatMessageDto>("/api/chat/send", req);
}

export async function markRead(otherUserId: string): Promise<number> {
  return postJson<number>(`/api/chat/mark-read/${encodeURIComponent(otherUserId)}`, {});
}

// -------- Presence --------
export type PeerPresence = { userId: string; online: boolean; lastSeen?: string | null };
export async function fetchPeersPresence(): Promise<Record<string, PeerPresence>> {
  try {
    const headers = await withAuth();
    const res = await fetch(U('/api/chat/presence/peers'), { headers });
    if (!res.ok) return {};
    const data: any = await res.json();
    const out: Record<string, PeerPresence> = {};
    const arr: any[] = Array.isArray(data?.peers) ? data.peers : [];
    for (const p of arr) {
      const id = String(p?.userId ?? p?.UserId ?? "");
      if (!id) continue;
      out[id] = { userId: id, online: !!(p?.online ?? p?.Online), lastSeen: p?.lastSeen ?? p?.LastSeen ?? null };
    }
    return out;
  } catch { return {}; }
}

/**
 * Upload a chat image. The 3rd param (clientMessageId) is optional but recommended
 * so the UI can reconcile the optimistic bubble immediately upon server echo.
 * If the server ignores/doesn't echo it back, the client still works and will
 * fall back to other reconciliation paths.
 */
export async function uploadImage(toId: string, file: File, clientMessageId?: number): Promise<ChatMessageDto> {
  const headers = await withAuth(); // auth only; DO NOT set Content-Type (browser sets multipart boundary)
  const form = new FormData();
  form.append("toId", toId);
  form.append("file", file);
  if (clientMessageId != null) form.append("clientMessageId", String(clientMessageId));

  const res = await fetch(U('/api/chat/image'), {
    method: "POST",
    headers,
    credentials: 'include',
    body: form,
  });

  // If your API doesn't have /api/chat/image yet, this will 404 -> let caller fallback
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`/api/chat/image ${res.status} ${txt}`);
  }
  return (await res.json()) as ChatMessageDto;
}

// -------------- Chat Image Library --------------
export type ChatLibraryItem = { id: number; url: string; key?: string | null; thumbUrl?: string | null; createdAt?: string };

export async function listChatLibrary(): Promise<{ items: ChatLibraryItem[]; max: number; count: number }> {
  const headers = await withAuth();
  const res = await fetch(U('/api/chat/library'), { headers, cache: 'no-store' });
  if (!res.ok) throw new Error(`/api/chat/library ${res.status}`);
  return res.json();
}

export async function uploadChatLibraryImage(file: File, thumb?: Blob): Promise<ChatLibraryItem> {
  const headers = await withAuth();
  const form = new FormData();
  form.append('file', file);
  if (thumb) form.append('thumb', thumb);
  const res = await fetch(U('/api/chat/library'), { method: 'POST', headers, credentials: 'include', body: form });
  if (res.status === 409) {
    const txt = await res.text().catch(() => '');
    throw new Error('library_full');
  }
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`/api/chat/library ${res.status} ${txt}`);
  }
  const data = await res.json();
  return (data?.item || data) as ChatLibraryItem;
}

export async function deleteChatLibraryImage(id: number): Promise<void> {
  const headers = await withAuth();
  const res = await fetch(U(`/api/chat/library/${encodeURIComponent(String(id))}`), { method: 'DELETE', headers });
  if (!res.ok && res.status !== 404) throw new Error(`/api/chat/library/${id} ${res.status}`);
}

// -------- Optional enhancements (tolerate 404) --------

export type UnfurlResult = {
  url: string;
  title?: string | null;
  site?: string | null;
  image?: string | null;
  description?: string | null;
};

export async function unfurl(url: string): Promise<UnfurlResult | null> {
  try {
    const headers = await withAuth();
    const res = await fetch(U(`/api/chat/unfurl?url=${encodeURIComponent(url)}`), { headers });
    if (!res.ok) return null; // 404 or others => just skip
    return (await res.json()) as UnfurlResult;
  } catch {
    return null;
  }
}

export async function react(messageId: number, emoji: string): Promise<boolean> {
  try {
    await postJson("/api/chat/react", { messageId, emoji });
    return true;
  } catch (e: any) {
    if (String(e?.message || "").includes("/api/chat/react 404")) return false;
    return false;
  }
}

export async function unreact(messageId: number, emoji: string): Promise<boolean> {
  try {
    const headers = await withAuth();
    const res = await fetch(
      U(`/api/chat/react?messageId=${encodeURIComponent(String(messageId))}&emoji=${encodeURIComponent(emoji)}`),
      {
        method: "DELETE",
        headers,
      }
    );
    if (!res.ok) return false;
    return true;
  } catch {
    return false;
  }
}

// -------- Moderation --------

export async function blockUser(userId: string): Promise<void> {
  const headers = await withAuth({ "Content-Type": "application/json" });
  const res = await fetch(U("/api/chat/block"), {
    method: "POST",
    headers,
    body: JSON.stringify({ userId }),
  });
  await throwIfNotOk(res, "/api/chat/block");
}
